import type { NextUpView } from '@contracts';
import { ArrowRight } from '@ui/shared/icons';
import { formatShortDate } from '@ui/shared/format/date';
import { EmptyCaption, StatusDotPill, TodayCard } from './primitives';
import type { NextUpItemView } from '@contracts';

/**
 * Row E — Next up · tomorrow (§5, design/screens/today-desktop-v2.md SS11).
 * Display-only, ordered preview — no checkbox, no interaction beyond
 * click-through. The DRAFT badge is a property of the preview card itself
 * (tomorrow is always provisional), reusing `AutosaveStatusPill`'s exact
 * draft-dot construction via the shared `StatusDotPill` primitive.
 *
 * `NextUpItemView` carries `objectiveTitle` but no `objectiveId` (contract,
 * domain-api.ts) — there is no per-item deep-link target to an objective
 * detail screen from this denormalized preview shape. The "Open" callout
 * link's target is therefore tomorrow's own Today page (`onOpenTomorrow`),
 * matching SS11's explicit "dev-ui's call, not load-bearing" allowance for
 * this exact ambiguity.
 */
export function NextUpTomorrowCard({
  tomorrowDate,
  nextUp,
  onOpenTomorrow,
}: {
  readonly tomorrowDate: string;
  readonly nextUp: NextUpView;
  readonly onOpenTomorrow: () => void;
}) {
  const first = nextUp.items[0] ?? null;

  return (
    <TodayCard label="Next up · tomorrow">
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <h2 style={{ margin: 0, fontSize: 'var(--type-heading-size)', fontWeight: 'var(--type-heading-weight)', color: 'var(--text-primary)' }}>
          Next up · {formatShortDate(tomorrowDate)}
        </h2>
        <StatusDotPill label="DRAFT" dotColor="var(--state-caution)" />
      </div>

      <div style={{ marginTop: 'var(--space-3)' }}>
        {nextUp.items.length === 0 ? (
          <EmptyCaption>Nothing lined up for tomorrow yet.</EmptyCaption>
        ) : (
          nextUp.items.map((item, i) => (
            <NextUpRow key={`${item.kind}-${item.id}`} item={item} isLast={i === nextUp.items.length - 1} />
          ))
        )}
      </div>

      {first && (
        <div
          style={{
            marginTop: 'var(--space-4)',
            paddingTop: 'var(--space-3)',
            borderTop: '1px solid var(--border-subtle)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--space-3)',
            background: 'var(--surface-hover)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--space-3)',
          }}
        >
          <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)' }}>
            Tomorrow&apos;s first ritual: {first.title}
          </span>
          <button
            type="button"
            onClick={onOpenTomorrow}
            style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', color: 'var(--hue-primary)', fontWeight: 600, fontSize: 'var(--type-caption-size)' }}
          >
            Open <ArrowRight size={14} aria-hidden="true" />
          </button>
        </div>
      )}
    </TodayCard>
  );
}

function NextUpRow({ item, isLast }: { readonly item: NextUpItemView; readonly isLast: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        padding: 'var(--space-2) 0',
        borderTop: '1px solid var(--border-subtle)',
        borderBottom: isLast ? '1px solid var(--border-subtle)' : 'none',
      }}
    >
      <span aria-hidden="true" style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--text-muted)', flex: 'none' }} />
      <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--type-body-size)', fontWeight: 500, color: 'var(--text-primary)' }}>{item.title}</span>
      {item.timeLabel && (
        <span className="num" style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)' }}>
          {item.timeLabel}
        </span>
      )}
    </div>
  );
}
